import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import SEO from '@/components/base/SEO';
import { articles, categories, faqs, testimonials } from '@/mocks/data';

const heroImages = [
  'https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20flowing%20organic%20shapes%20in%20amber%20terracotta%20and%20cream%20tones%2C%20minimal%20artistic%20composition%2C%20soft%20atmospheric%20light%2C%20elegant%20modern%20design%2C%20no%20text%20no%20people%2C%20sophisticated%20texture%20layers%20with%20gentle%20curves%20and%20floating%20geometric%20elements&width=1600&height=900&seq=hero-main-01&orientation=landscape',
];

export default function HomePage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentSlide, setCurrentSlide] = useState(0);

  const featuredArticles = articles.filter(a => a.featured);
  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter(a => a.category === activeCategory);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % featuredArticles.length);
  }, [featuredArticles.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '精品一区二区',
    url: 'https://jingpinyiquercu.com/',
    description: '精品一区二区汇聚全网优质内容资源，严格分区筛选，为您提供高品质的一区二区精选内容体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://jingpinyiquercu.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '精品一区二区',
    url: 'https://jingpinyiquercu.com/',
    description: '精品一区二区是一个高品质内容聚合平台，按分区整理和展示精选内容资源。',
    sameAs: [
      'https://jingpinyiquercu.com/',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="精品一区二区·优选内容聚合平台 - 高品质分区精选内容"
        description="精品一区二区汇聚全网优质内容资源，严格分区筛选，为您提供高品质的一区二区精选内容体验。无论您寻找哪类精品资源，均按区域分类整理，清晰直观，轻松找到所需内容。"
        keywords="精品一区二区,精品一区,精品二区,精品分区内容,优质分区资源,精品内容聚合"
        schemaData={{ ...schemaData, ...orgSchema }}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[520px] md:h-[650px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImages[0]}
              alt="精品一区二区"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          </div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
            <span className="inline-block px-4 py-1.5 text-xs font-medium bg-primary-500/90 text-background-50 rounded-full mb-6 whitespace-nowrap">
              优选内容聚合平台
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-background-50 mb-5 max-w-4xl leading-tight">
              精品一区二区 · 高品质<span className="text-primary-400">分区精选</span>内容
            </h1>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mb-8 leading-relaxed">
              汇聚全网优质内容资源，严格分区筛选，按区域分类整理，清晰直观，轻松找到您所需的每一篇精品内容。
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/category/yiqu"
                className="px-8 py-3.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                探索精品一区
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/category/ercu"
                className="px-8 py-3.5 bg-background-50/15 text-background-50 border border-background-50/30 text-sm font-medium rounded-full hover:bg-background-50/25 transition-all duration-200 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                探索精品二区
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
            <div className="flex items-center gap-2 mt-10">
              {featuredArticles.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    i === currentSlide ? 'w-6 bg-primary-500' : 'bg-background-50/40 hover:bg-background-50/60'
                  }`}
                  aria-label={`第${i + 1}张`}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                精选推荐
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
                编辑团队精心挑选的本期优质内容，涵盖科技、商业、生活、文化等多个领域
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredArticles.map(article => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                分区浏览
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
                按区域分类整理，让您快速定位感兴趣的内容领域
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {categories.map(cat => {
                const catArticles = articles.filter(a => a.category === cat.slug);
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group relative overflow-hidden rounded-2xl bg-background-50 border border-background-200/70 p-8 md:p-10 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl mb-6 ${
                      cat.color === 'accent' ? 'bg-accent-100 text-accent-600' : 'bg-primary-100 text-primary-600'
                    }`}>
                      <i className={`${cat.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                      {cat.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">
                        共 {cat.articleCount} 篇文章
                      </span>
                      <span className={`inline-flex items-center text-sm font-medium whitespace-nowrap group-hover:gap-2 transition-all duration-200 ${
                        cat.color === 'accent' ? 'text-accent-600' : 'text-primary-600'
                      }`}>
                        浏览全部
                        <i className="ri-arrow-right-line ml-1"></i>
                      </span>
                    </div>
                    <div className={`absolute -bottom-6 -right-6 w-32 h-32 rounded-full opacity-10 ${
                      cat.color === 'accent' ? 'bg-accent-500' : 'bg-primary-500'
                    }`}></div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                  最新文章
                </h2>
                <p className="text-sm text-foreground-500">
                  持续更新优质内容，与您分享知识与见解
                </p>
              </div>
              <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1">
                {[
                  { key: 'all', label: '全部' },
                  ...categories.map(c => ({ key: c.slug, label: c.name })),
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveCategory(tab.key)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === tab.key
                        ? 'bg-primary-500 text-background-50 shadow-sm'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map(article => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
            {filteredArticles.length === 0 && (
              <div className="text-center py-16 text-foreground-400">
                <i className="ri-inbox-line text-4xl mb-3 block"></i>
                <p className="text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
                听听用户们怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map(t => (
                <div
                  key={t.id}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-6"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 whitespace-nowrap">{t.name}</p>
                      <p className="text-xs text-foreground-400 whitespace-nowrap">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
                关于精品一区二区的常见疑问解答
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-20 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20with%20flowing%20organic%20shapes%20in%20amber%20and%20cream%20tones%2C%20soft%20atmospheric%20light%2C%20minimal%20elegant%20composition%2C%20no%20text%2C%20sophisticated%20texture%20layers%20with%20gentle%20flowing%20curves&width=1600&height=600&seq=cta-bg-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-4">
              开始您的精品阅读之旅
            </h2>
            <p className="text-sm md:text-base text-background-200 max-w-lg mx-auto mb-8 leading-relaxed">
              加入精品一区二区，发现更多高品质精选内容，让优质阅读成为生活的一部分。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/yiqu"
                className="px-8 py-3.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                立即浏览
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 bg-background-50/15 text-background-50 border border-background-50/30 text-sm font-medium rounded-full hover:bg-background-50/25 transition-all duration-200 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}